import React, { useEffect, useState } from "react";
import axios from "axios";
import { jwtDecode } from "jwt-decode";


const Departments = () => {
  const [departments, setDepartments] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingDeptId, setEditingDeptId] = useState(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');

  const [formData, setFormData] = useState({
    name: "",
    companyId: ""
  });

  useEffect(() => {
    fetchCompanies();
  }, []);

  useEffect(() => {
    if (selectedCompanyId) {
      fetchDepartments(selectedCompanyId);
    }
  }, [selectedCompanyId]);

  const fetchCompanies = async () => {
  const token = localStorage.getItem("authToken");
  if (!token) {
    setError("No access token found. Please log in.");
    setLoading(false);
    return;
  }

  let decoded;
  try {
    decoded = jwtDecode(token);
  } catch (err) {
    console.error("Invalid token:", err);
    setError("Invalid token. Please log in again.");
    setLoading(false);
    return;
  }

  // Extract companyId (your backend must include it in the token)
  const companyId = decoded.companyId || decoded.id;  // depends on your payload structure
  if (!companyId) {
    setError("No companyId found in token.");
    setLoading(false);
    return;
  }

  try {
    const response = await axios.get(
      `https://customersupport-jhy9.onrender.com/api/companies/${companyId}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );

    setCompanies([response.data]);
    setSelectedCompanyId(companyId);
  } catch (err) {
    console.error("Error fetching companies:", err);
    setError("Failed to fetch companies.");
  } finally {
    setLoading(false);
  }
};


  const handleCompanyChange = (e) => {
    const companyId = e.target.value;
    setSelectedCompanyId(companyId);
    localStorage.setItem("selectedCompanyId", companyId);
  };

  const fetchDepartments = async (companyId) => {
    if (!companyId) return;
    const token = localStorage.getItem("authToken");
    setLoading(true);

    try {
      const response = await axios.get(
        `https://customersupport-jhy9.onrender.com/api/departments/company/${companyId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDepartments(response.data);
      setError(null);
    } catch (err) {
      console.error("Error fetching departments:", err);
      setDepartments([]);
      setError("Failed to fetch departments for the selected company.");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: value,
    }));
  };

  const handleAddDepartment = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("authToken");
    const companyId = selectedCompanyId;

    if (!companyId) {
      alert("Please select a company first.");
      return;
    }

    try {
      const response = await axios.post(
        `https://customersupport-jhy9.onrender.com/api/departments/company/${companyId}`,
        { name: formData.name },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      console.log("Department created:", response.data);
      fetchDepartments(companyId);
      resetForm();
    } catch (err) {
      console.error("Error creating department:", err.response?.data || err.message);
      alert(err.response?.data?.message || "Error creating department");
    }
  };

  const handleEditDepartment = (dept) => {
    setIsEditing(true);
    setEditingDeptId(dept._id);
    setFormData({
      name: dept.name || "",
      companyId: dept.company || selectedCompanyId
    });
    setShowForm(true);
  };

  const handleUpdateDepartment = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("authToken");

    try {
      const response = await axios.put(
        `https://customersupport-jhy9.onrender.com/api/departments/${editingDeptId}`,
        { 
          name: formData.name,
          company: formData.companyId || selectedCompanyId
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      console.log("Department updated:", response.data);
      fetchDepartments(selectedCompanyId);
      resetForm();
    } catch (err) {
      console.error("Error updating department:", err.response?.data || err.message);
      alert("Error updating department");
    }
  };

  const handleDeleteDepartment = async (id) => {
    if (!window.confirm("Are you sure you want to delete this department?")) return;
    const token = localStorage.getItem("authToken");

    try {
      await axios.delete(`https://customersupport-jhy9.onrender.com/api/departments/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      fetchDepartments(selectedCompanyId);
    } catch (err) {
      console.error("Error deleting department:", err.response?.data || err.message);
      alert("Error deleting department");
    }
  };

  const resetForm = () => {
    setFormData({ 
      name: "",
      companyId: selectedCompanyId || ""
    });
    setIsEditing(false);
    setEditingDeptId(null);
    setShowForm(false);
  };

  if (loading && !departments.length) return <p className="text-center">Loading...</p>;
  if (error) return <p className="text-center text-red-500">{error}</p>;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-8 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <h1 className="text-3xl font-bold text-gray-800">Department Management</h1>
          
          {/* Company Selector */}
          <div className="mt-4 md:mt-0">
            <div className="px-4 py-2 bg-white border border-gray-300 rounded-md shadow-sm">
              {companies[0]?.businessName || 'Loading company...'}
            </div>
          </div>
          
          <button
            onClick={() => (showForm ? resetForm() : setShowForm(true))}
            className="mt-4 md:mt-0 px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-md"
          >
            {showForm ? "Cancel" : "Add Department"}
          </button>
        </div>

        {/* Form */}
        {showForm && (
          <div className="bg-white rounded-xl shadow-md p-6 mb-8">
            <h2 className="text-xl font-semibold mb-6">
              {isEditing ? "Edit Department" : "Add New Department"}
            </h2>
            <form onSubmit={isEditing ? handleUpdateDepartment : handleAddDepartment}>
              <div className="grid grid-cols-1 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Department Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                    required
                  />
                </div>
                
                {isEditing && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Company
                    </label>
                    <select
                      name="companyId"
                      value={formData.companyId || selectedCompanyId}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                      required
                    >
                      {companies.map((company) => (
                        <option key={company._id} value={company._id}>
                          {company.businessName}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={resetForm}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                  >
                    {isEditing ? "Update Department" : "Add Department"}
                  </button>
                </div>
              </div>
            </form>
          </div>
        )}

        {/* Departments List */}
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-800">Departments</h2>
          </div>
          <div className="divide-y divide-gray-200">
            {departments.length === 0 ? (
              <div className="p-6 text-center text-gray-500">
                No departments found. Add a department to get started.
              </div>
            ) : (
              departments.map((dept) => (
                <div key={dept._id} className="p-6 hover:bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-medium text-gray-900">{dept.name}</h3>
                      <p className="text-sm text-gray-500">
                        Company: {companies.find(c => c._id === dept.company)?.businessName || 'N/A'}
                      </p>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleEditDepartment(dept)}
                        className="px-3 py-1 text-sm text-indigo-600 hover:text-indigo-900"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteDepartment(dept._id)}
                        className="px-3 py-1 text-sm text-red-600 hover:text-red-900"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Departments;
